import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, Spinner, Pagination } from "react-bootstrap";
import { API_BASE } from "../Config";
import PermissionWrapper from "../components/PermissionWrapper";
import axiosInstance from "../services/BaseService";
import BookingModal from "../components/BookingModal";
import CommunicationService from "../services/CommunicationService";
import CustomerSupportWidget from "../components/CustomerSupportWidget";

// Add sharing functions for events and programs
const shareEvent = async (eventData: any) => {
  try {
    const response = await axiosInstance.post('/tickets/support/communication-links', {
      templateType: 'EVENT_SHARING',
      variables: {
        eventName: eventData.eventName,
        eventDate: eventData.eventDate,
        eventTime: eventData.eventTime,
        venue: eventData.venue,
        bookingUrl: eventData.bookingUrl || window.location.href
      }
    });
    
    if (response.data.links?.whatsapp) {
      window.open(response.data.links.whatsapp.link, '_blank');
    }
  } catch (error) {
    console.error('Error sharing event:', error);
  }
};

const shareProgram = async (programData: any) => {
  try {
    const response = await axiosInstance.post('/tickets/support/communication-links', {
      templateType: 'PROGRAM_SHARING',
      variables: {
        programTitle: programData.programTitle,
        groupName: programData.groupName,
        programGenre: programData.programGenre,
        programDuration: programData.programDuration,
        programDescription: programData.programDescription,
        programUrl: programData.programUrl || window.location.href
      }
    });
    
    if (response.data.links?.whatsapp) {
      window.open(response.data.links.whatsapp.link, '_blank');
    }
  } catch (error) {
    console.error('Error sharing program:', error);
  }
};

const ManageAllBookingsPage: React.FC = () => {
  // Hierarchical filter states
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [plays, setPlays] = useState<any[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [months, setMonths] = useState<number[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  
  // Selected filter values
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedPlay, setSelectedPlay] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredBookings, setFilteredBookings] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [scheduleStats, setScheduleStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    totalRevenue: 0,
    totalSeats: 0,
    availableSeats: 0,
    bookedSeats: 0
  });

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const countryMap: { [key: string]: string } = {
    "IN": "India",
    "US": "United States",
    "UK": "United Kingdom",
    "CA": "Canada",
    "AU": "Australia"
  };

  const stateMap: { [key: string]: string } = {
    "MH": "Maharashtra",
    "KA": "Karnataka",
    "TN": "Tamil Nadu",
    "DL": "Delhi",
    "GJ": "Gujarat",
    "RJ": "Rajasthan",
    "UP": "Uttar Pradesh",
    "WB": "West Bengal",
    "AP": "Andhra Pradesh",
    "TS": "Telangana",
    "KL": "Kerala",
    "OR": "Odisha",
    "PB": "Punjab",
    "HR": "Haryana",
    "AS": "Assam",
    "BR": "Bihar",
    "CG": "Chhattisgarh",
    "GA": "Goa",
    "HP": "Himachal Pradesh",
    "JH": "Jharkhand",
    "MP": "Madhya Pradesh",
    "MN": "Manipur",
    "ML": "Meghalaya",
    "MZ": "Mizoram",
    "NL": "Nagaland",
    "SK": "Sikkim",
    "TR": "Tripura",
    "UK": "Uttarakhand",
    "AN": "Andaman and Nicobar Islands",
    "CH": "Chandigarh",
    "DN": "Dadra and Nagar Haveli",
    "DD": "Daman and Diu",
    "LD": "Lakshadweep",
    "PY": "Puducherry"
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get('/groups/distinct/countries');
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStatesByCountry = async (country: string) => {
    try {
      const response = await axiosInstance.get(`/groups/distinct/states/${country}`);
      setStates(response.data);
    } catch (error) {
      setStates([]);
    }
  };

  const fetchGroupsByLocation = async (country: string, state: string) => {
    try {
      const response = await axiosInstance.get('/groups/filter', {
        params: { country, state }
      });
      setGroups(response.data.groups || response.data);
    } catch (error) {
      setGroups([]);
    }
  };

  const fetchPlaysByGroup = async (groupId: string) => {
    try {
      const response = await axiosInstance.get(`/programs/group/${groupId}`);
      const playData = response.data["programs"];
      setPlays(playData);
    } catch (error) {
      setPlays([]);
    }
  };

  const fetchYearsByPlay = async (playId: string) => {
    try {
      console.log('Fetching years for play:', playId);
      const response = await axiosInstance.get('/programschedules/distinct/years', {
        params: { programId: playId }
      });
      console.log('Years response:', response.data);
      setYears(response.data);
    } catch (error) {
      console.error('Error fetching years:', error);
      setYears([]);
    }
  };

  const fetchMonthsByYear = async (year: string) => {
    try {
      console.log('Fetching months for year:', year, 'play:', selectedPlay);
      const response = await axiosInstance.get(`/programschedules/distinct/months/${year}`, {
        params: { programId: selectedPlay }
      });
      console.log('Months response:', response.data);
      setMonths(response.data);
    } catch (error) {
      console.error('Error fetching months:', error);
      setMonths([]);
    }
  };

  const fetchSchedulesByFilters = async () => {
    try {
      const params: any = {};
      if (selectedPlay) params.programId = selectedPlay;
      if (selectedYear) params.year = selectedYear;
      if (selectedMonth) params.month = selectedMonth;
      
      console.log('Fetching schedules with params:', params);
      const response = await axiosInstance.get('/programschedules/filter', { params });
      console.log('Schedules response:', response.data);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchBookingsBySchedule = async (scheduleId: string) => {
    try {
      setLoading(true);
      const [bookingsResponse, statsResponse] = await Promise.all([
        axiosInstance.get(`/tickets/booking-enquiries/schedule/${scheduleId}`),
        axiosInstance.get(`/tickets/stats/schedule/${scheduleId}`)
      ]);
      setBookings(bookingsResponse.data);
      setFilteredBookings(bookingsResponse.data);
      setScheduleStats(statsResponse.data);
    } catch (error) {
      setError('Failed to fetch bookings');
      setBookings([]);
      setFilteredBookings([]);
      setScheduleStats({
        totalBookings: 0,
        pendingBookings: 0,
        confirmedBookings: 0,
        totalRevenue: 0,
        totalSeats: 0,
        availableSeats: 0,
        bookedSeats: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedState("");
    setSelectedGroup("");
    setSelectedPlay("");
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedSchedule("");
    setStates([]);
    setGroups([]);
    setPlays([]);
    setYears([]);
    setMonths([]);
    setSchedules([]);
    setBookings([]);
    
    if (country) {
      fetchStatesByCountry(country);
    }
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setSelectedGroup("");
    setSelectedPlay("");
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedSchedule("");
    setGroups([]);
    setPlays([]);
    setYears([]);
    setMonths([]);
    setSchedules([]);
    setBookings([]);
    
    if (state && selectedCountry) {
      fetchGroupsByLocation(selectedCountry, state);
    }
  };

  const handleGroupChange = (groupId: string) => {
    setSelectedGroup(groupId);
    setSelectedPlay("");
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedSchedule("");
    setPlays([]);
    setYears([]);
    setMonths([]);
    setSchedules([]);
    setBookings([]);
    
    if (groupId) {
      fetchPlaysByGroup(groupId);
    }
  };

  const handlePlayChange = (playId: string) => {
    setSelectedPlay(playId);
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedSchedule("");
    setYears([]);
    setMonths([]);
    setSchedules([]);
    setBookings([]);
    
    if (playId) {
      fetchYearsByPlay(playId);
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setSelectedMonth("");
    setSelectedSchedule("");
    setMonths([]);
    setSchedules([]);
    setBookings([]);
    
    if (year) {
      fetchMonthsByYear(year);
    }
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    setSelectedSchedule("");
    setSchedules([]);
    setBookings([]);
    
    if (month) {
      fetchSchedulesByFilters();
    }
  };

  const handleScheduleChange = (scheduleId: string) => {
    setSelectedSchedule(scheduleId);
    setBookings([]);
    
    if (scheduleId) {
      fetchBookingsBySchedule(scheduleId);
    }
  };

  const applyFilters = (searchTerm: string, statusFilter: string) => {
    let filtered = bookings;
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((booking: any) => booking.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((booking: any) => 
        booking.bookingCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.phoneNumber?.includes(searchTerm) ||
        booking.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredBookings(filtered);
    setCurrentPage(1);
  };

  const getPaginatedBookings = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredBookings.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const generateTicketText = (booking: any) => {
    const schedule = schedules.find(s => s._id === selectedSchedule);
    const ticketDetails = booking.tickets.map((ticket: any) => 
      `${ticket.ticketOptionId?.ticketName || 'N/A'}: ${ticket.quantity}`
    ).join('\n');
    
    return `🎭 TICKET CONFIRMATION\n\nBooking Code: ${booking.bookingCode}\nName: ${booking.customerName}\nPhone: ${booking.phoneNumber}\n\nShow: ${schedule?.programId?.title}\nDate: ${new Date(schedule?.startDate).toLocaleDateString()}\nTime: ${schedule?.timing}\nVenue: ${schedule?.address}\n\nTickets:\n${ticketDetails}\n\nTotal Amount: ₹${booking.totalAmount}\nStatus: CONFIRMED\n\nPresent this ticket at the venue.`;
  };

  const downloadTicket = async (booking: any) => {
    try {
      const response = await axiosInstance.get(`/ticket-image/ticket-image/${booking._id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${booking.bookingCode}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download ticket:', error);
      setError('Failed to download ticket image');
    }
  };

  const shareBookingConfirmation = async (booking: any, action: 'confirm' | 'cancel' | 'share' = 'share') => {
    try {
      if (action === 'share') {
        // Use BOOKING_SHARE template for sharing booking details
        const schedule = schedules.find(s => s._id === selectedSchedule);
        const response = await axiosInstance.post('/communication/device-links', {
          templateType: 'BOOKING_SHARE',
          recipient: {
            phone: booking.phoneNumber,
            email: booking.email
          },
          variables: {
            customerName: booking.customerName,
            eventName: schedule?.programId?.title || 'Event',
            eventDate: schedule?.startDate ? new Date(schedule.startDate).toLocaleDateString() : 'TBD',
            eventTime: schedule?.timing || 'TBD',
            venue: schedule?.address || 'TBD',
            seatNumbers: booking.tickets?.map((t: any) => `${t.ticketOptionId?.ticketName} (${t.quantity})`).join(', ') || 'N/A',
            amount: booking.totalAmount,
            bookingCode: booking.bookingCode
          }
        });
        
        if (response.data.links?.whatsapp) {
          window.open(response.data.links.whatsapp.link, '_blank');
          return;
        }
      } else {
        const response = await axiosInstance.get(`/tickets/booking-enquiry/${booking._id}/communication-links?action=${action}`);
        const { links } = response.data;
        
        if (links.whatsapp) {
          window.open(links.whatsapp.link, '_blank');
          return;
        }
      }
    } catch (error) {
      console.error('Error getting communication links:', error);
    }
    
    // Fallback to original method
    const schedule = schedules.find(s => s._id === selectedSchedule);
    if (!schedule) return;
    
    try {
      await CommunicationService.shareBooking(
        booking.bookingCode,
        schedule.programId?.title || 'Event',
        new Date(schedule.startDate).toLocaleDateString(),
        schedule.timing,
        schedule.address,
        booking.tickets.map((t: any) => `${t.quantity}x ${t.ticketOptionId?.ticketName}`).join(', '),
        booking.totalAmount,
        booking.customerName,
        'whatsapp'
      );
    } catch (error) {
      console.error('Error sharing booking:', error);
    }
  };

  const contactCustomer = async (booking: any, issueType: string) => {
    try {
      const response = await axiosInstance.post('/communication/device-links', {
        templateType: 'CUSTOMER_SUPPORT',
        recipient: {
          phone: booking.phoneNumber,
          email: booking.email
        },
        variables: {
          customerName: booking.customerName || 'Customer',
          bookingCode: booking.bookingCode,
          issueType,
          eventName: schedules.find(s => s._id === selectedSchedule)?.programId?.title || 'Event'
        }
      });
      const { links } = response.data;
      
      if (links.whatsapp) {
        window.open(links.whatsapp.link, '_blank');
        return;
      }
    } catch (error) {
      console.error('Error getting support communication links:', error);
    }
    
    // Fallback to original method
    const fallbackMessage = `Hello ${booking.customerName || 'Customer'},\n\nRegarding your booking ${booking.bookingCode} for ${schedules.find(s => s._id === selectedSchedule)?.programId?.title}.\n\nIssue: ${issueType}\n\nHow can we help you?`;
    const whatsappUrl = `https://wa.me/${booking.phoneNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(fallbackMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    applyFilters(term, statusFilter);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    applyFilters(searchTerm, status);
  };

  const updateBookingStatus = async (bookingId: string, status: string) => {
    try {
      const response = await axiosInstance.patch(`/tickets/booking-enquiry/${bookingId}/status`, { status });
      
      if (response.status === 200) {
        setSuccess(`Booking ${status} successfully`);
        fetchBookingsBySchedule(selectedSchedule);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError('Failed to update booking status');
    }
  };

  return (
    <PermissionWrapper pageName="ManageAllBookingsPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2><i className="bi bi-ticket-perforated me-2"></i>Manage All Bookings</h2>
          </Col>
        </Row>

        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0"><i className="bi bi-funnel me-2"></i>Filter Bookings</h5>
          </Card.Header>
          <Card.Body className="p-4">
            {/* Location Filters */}
            <div className="mb-4">
              <h6 className="text-muted mb-3"><i className="bi bi-geo-alt me-2"></i>Location</h6>
              <Row className="g-3">
                <Col sm={6} md={4}>
                  <Form.Select value={selectedCountry} onChange={(e) => handleCountryChange(e.target.value)} className="border-2">
                    <option value="">Country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{countryMap[country] || country}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col sm={6} md={4}>
                  <Form.Select value={selectedState} onChange={(e) => handleStateChange(e.target.value)} disabled={!selectedCountry} className="border-2">
                    <option value="">State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{stateMap[state] || state}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col sm={12} md={4}>
                  <Form.Select value={selectedGroup} onChange={(e) => handleGroupChange(e.target.value)} disabled={!selectedState} className="border-2">
                    <option value="">Group</option>
                    {groups.map(group => (
                      <option key={group._id} value={group._id}>{group.name}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </div>

            {/* Program & Schedule Filters */}
            <div className="mb-4">
              <h6 className="text-muted mb-3"><i className="bi bi-calendar-event me-2"></i>Program & Schedule</h6>
              <Row className="g-3">
                <Col sm={12} md={6}>
                  <Form.Select value={selectedPlay} onChange={(e) => handlePlayChange(e.target.value)} disabled={!selectedGroup} className="border-2">
                    <option value="">Program/Play</option>
                    {plays.map(play => (
                      <option key={play._id} value={play._id}>{play.title}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col sm={6} md={3}>
                  <Form.Select value={selectedYear} onChange={(e) => handleYearChange(e.target.value)} disabled={!selectedPlay} className="border-2">
                    <option value="">Year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col sm={6} md={3}>
                  <Form.Select value={selectedMonth} onChange={(e) => handleMonthChange(e.target.value)} disabled={!selectedYear} className="border-2">
                    <option value="">Month</option>
                    {months.map(month => (
                      <option key={month} value={month}>{monthNames[month - 1]}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </div>

            {/* Schedule Selection */}
            <div>
              <h6 className="text-muted mb-3"><i className="bi bi-clock me-2"></i>Schedule</h6>
              <Form.Select value={selectedSchedule} onChange={(e) => handleScheduleChange(e.target.value)} disabled={!selectedMonth} className="border-2">
                <option value="">Choose Schedule...</option>
                {schedules.map(schedule => (
                  <option key={schedule._id} value={schedule._id}>
                    {new Date(schedule.startDate).toLocaleDateString()} - {schedule.timing} - {schedule.address}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {selectedSchedule && (
          <>
            {/* Schedule Stats */}
            <Row className="mb-4 g-3">
              <Col xs={6} md={3}>
                <Card className="text-center h-100 border-0 shadow-sm">
                  <Card.Body className="p-3">
                    <i className="bi bi-calendar-check display-6 text-success"></i>
                    <h5 className="mt-2 mb-1">{scheduleStats.totalBookings}</h5>
                    <p className="text-muted small mb-0">Total Bookings</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center h-100 border-0 shadow-sm">
                  <Card.Body className="p-3">
                    <i className="bi bi-currency-rupee display-6 text-primary"></i>
                    <h5 className="mt-2 mb-1">₹{scheduleStats.totalRevenue}</h5>
                    <p className="text-muted small mb-0">Revenue</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center h-100 border-0 shadow-sm">
                  <Card.Body className="p-3">
                    <i className="bi bi-people display-6 text-info"></i>
                    <h5 className="mt-2 mb-1">{scheduleStats.bookedSeats}/{scheduleStats.totalSeats}</h5>
                    <p className="text-muted small mb-0">Seats Sold</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center h-100 border-0 shadow-sm">
                  <Card.Body className="p-3">
                    <i className="bi bi-clock display-6 text-warning"></i>
                    <h5 className="mt-2 mb-1">{scheduleStats.pendingBookings}</h5>
                    <p className="text-muted small mb-0">Pending</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card>
              <Card.Header>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
                  <h5 className="mb-0">Booking Enquiries ({filteredBookings.length})</h5>
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                  <small className="text-muted text-center text-md-start">
                    {schedules.find(s => s._id === selectedSchedule)?.programId?.title} - 
                    {schedules.find(s => s._id === selectedSchedule) && new Date(schedules.find(s => s._id === selectedSchedule)!.startDate).toLocaleDateString()}
                  </small>
                  <Button variant="primary" size="sm" onClick={() => setShowBookingModal(true)} className="w-100 w-md-auto">
                    <i className="bi bi-plus me-1"></i>Create Booking
                  </Button>
                </div>
              </div>
              <Row className="g-2">
                <Col xs={12} md={7}>
                  <Form.Control
                    type="text"
                    placeholder="Search by booking code, name, phone, or email..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={5}>
                  <Form.Select value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)} size="sm">
                    <option value="all">All Bookings</option>
                    <option value="pending">Pending Only</option>
                    <option value="confirmed">Confirmed Only</option>
                    <option value="cancelled">Cancelled Only</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-3">
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : getPaginatedBookings().length > 0 ? (
                <>
                  {/* Card View Only */}
                  <Row className="g-3">
                    {getPaginatedBookings().map((booking: any) => (
                      <Col xs={12} sm={6} lg={4} key={booking._id}>
                        <Card className="h-100 shadow-sm border-0" style={{ borderLeft: `4px solid ${booking.status === 'confirmed' ? '#198754' : booking.status === 'cancelled' ? '#dc3545' : '#ffc107'}` }}>
                          <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="text-primary mb-1">{booking.bookingCode || 'N/A'}</h6>
                                <div className="text-muted small">{booking.customerName || 'N/A'}</div>
                              </div>
                              <Badge bg={booking.status === 'confirmed' ? 'success' : booking.status === 'cancelled' ? 'danger' : 'warning'} className="px-2 py-1">
                                {booking.status.toUpperCase()}
                              </Badge>
                            </div>
                            
                            <div className="mb-2">
                              <i className="bi bi-telephone-fill text-primary me-2"></i>
                              <strong>{booking.phoneNumber}</strong>
                            </div>
                            
                            {booking.email && (
                              <div className="mb-2">
                                <i className="bi bi-envelope-fill text-info me-2"></i>
                                <small>{booking.email}</small>
                              </div>
                            )}
                            
                            <div className="mb-3">
                              <div className="small text-muted mb-1"><i className="bi bi-ticket-perforated me-1"></i>Tickets:</div>
                              {booking.tickets.map((ticket: any, index: number) => (
                                <div key={index} className="small bg-light p-1 rounded mb-1">
                                  <strong>{ticket.ticketOptionId?.ticketName || 'N/A'}</strong>: {ticket.quantity}
                                </div>
                              ))}
                            </div>
                            
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="h5 text-success mb-0">₹{booking.totalAmount}</div>
                                <div className="small text-muted">
                                  <i className="bi bi-calendar3 me-1"></i>
                                  {new Date(booking.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                              <div className="d-flex gap-1 flex-wrap">
                                {(booking.status === 'pending' || booking.status === 'verification-pending') && (
                                  <>
                                    <Button size="sm" variant="success" onClick={() => updateBookingStatus(booking._id, 'confirmed')} title="Confirm">
                                      <i className="bi bi-check-lg"></i>
                                    </Button>
                                    <Button size="sm" variant="danger" onClick={() => updateBookingStatus(booking._id, 'cancelled')} title="Cancel">
                                      <i className="bi bi-x-lg"></i>
                                    </Button>
                                    <Button size="sm" variant={booking.status === 'verification-pending' ? 'danger' : 'success'} onClick={() => contactCustomer(booking, booking.status === 'verification-pending' ? 'Payment Verification Issue' : 'Payment Request')} title={booking.status === 'verification-pending' ? 'Contact Customer' : 'Request Payment'}>
                                      <i className="bi bi-whatsapp"></i>
                                    </Button>
                                  </>
                                )}
                                {booking.status === 'confirmed' && (
                                  <>
                                    <Button size="sm" variant="primary" onClick={() => downloadTicket(booking)} title="Download Ticket Image">
                                      <i className="bi bi-image"></i>
                                    </Button>
                                    <Button size="sm" variant="success" onClick={() => shareBookingConfirmation(booking, 'share')} title="Share Booking Details">
                                      <i className="bi bi-whatsapp"></i>
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => {
                                      updateBookingStatus(booking._id, 'cancelled');
                                      shareBookingConfirmation(booking, 'cancel');
                                    }} title="Cancel Booking">
                                      <i className="bi bi-x-circle"></i>
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <Pagination>
                        <Pagination.Prev 
                          disabled={currentPage === 1} 
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                        {[...Array(totalPages)].map((_, index) => (
                          <Pagination.Item
                            key={index + 1}
                            active={index + 1 === currentPage}
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next 
                          disabled={currentPage === totalPages} 
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-inbox display-1 text-muted"></i>
                  <p className="text-muted mt-3">No bookings found</p>
                </div>
              )}
            </Card.Body>
          </Card>
          </>
        )}

        {!selectedSchedule && (
          <Card>
            <Card.Body className="text-center py-5">
              <i className="bi bi-funnel display-4 text-muted"></i>
              <h5 className="text-muted mt-3">Select Filters to View Bookings</h5>
              <p className="text-muted">Choose Country → State → Group → Program → Year → Month → Schedule to view booking enquiries</p>
            </Card.Body>
          </Card>
        )}

        {/* Booking Modal */}
        <BookingModal
          show={showBookingModal}
          onHide={() => {
            setShowBookingModal(false);
            if (selectedSchedule) {
              fetchBookingsBySchedule(selectedSchedule);
            }
          }}
          schedule={schedules.find(s => s._id === selectedSchedule)}
          disableAutoPopulate={true}
        />
        
        {/* Customer Support Widget */}
        {/* <CustomerSupportWidget position="bottom-right" /> */}
      </Container>
    </PermissionWrapper>
  );
};

export default ManageAllBookingsPage;